/**
 * InstructionsTab — Plain-English guide to the Settled Loans CRM.
 *
 * Goal: when a brand-new broker / officer logs in, they can read this
 * page and understand the workflow end-to-end in 5 minutes. Two halves:
 *
 *   1. "Load demo data" — one click and they have 2 realistic loans
 *      (Sarah Mitchell at CBA, Daniel Chen at Bluestone) populated with
 *      care tasks, an NPS response and an opportunity. Lets them poke
 *      around the layout without messing up production data.
 *   2. The "How it works" guide — every tab explained, the core
 *      workflows (copy-paste email, snooze/done, closing a loan,
 *      specialist refinance pathway), and key concepts (cadence,
 *      suppression, audit log, NPS branches).
 */
import React, { useEffect, useState } from 'react';
import {
  Loader2, BookOpen, PlayCircle, Trash2, CheckCircle2, ArrowRight,
  Building2, Zap, Target, ListChecks, AlertTriangle, ShieldAlert, FileText, Mail,
  Sparkles, Copy, UserCheck,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const InstructionsTab = () => {
  const { token, user } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [seeding, setSeeding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [demoCount, setDemoCount] = useState(null);

  const isAdmin = ['admin', 'manager', 'broker'].includes((user?.role || '').toLowerCase());

  // Probe whether demo data is currently present so the button label tells the truth.
  const checkDemoCount = async () => {
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const j = await r.json();
      const list = Array.isArray(j) ? j : (j.loans || j.items || []);
      setDemoCount(list.filter((l) => l.is_demo).length);
    } catch { /* silent */ }
  };

  useEffect(() => { checkDemoCount(); }, []);

  const loadDemo = async () => {
    setSeeding(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/demo/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Could not load demo data');
      const j = await r.json();
      if (j.already_seeded) {
        toast.info('Demo loans already loaded — head to the Loan book tab.');
      } else {
        toast.success(`Demo loaded — ${j.demo_loans_created} loans + care tasks + NPS + an opportunity. Check the Loan book tab.`);
      }
      await checkDemoCount();
    } catch (e) { toast.error(e.message); } finally { setSeeding(false); }
  };

  const removeDemo = async () => {
    if (!window.confirm('Remove all demo data (loans, tasks, activities, NPS, opportunities)? Your real data is not touched.')) return;
    setRemoving(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/demo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Could not remove demo data');
      const j = await r.json();
      toast.success(`Removed ${j.removed} demo loan${j.removed === 1 ? '' : 's'}.`);
      await checkDemoCount();
    } catch (e) { toast.error(e.message); } finally { setRemoving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid="instructions-tab">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-emerald-50 dark:from-blue-950/30 dark:via-indigo-950/30 dark:to-emerald-950/30 border border-blue-100 dark:border-blue-900/40 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-3">
          <BookOpen className="w-7 h-7 text-blue-600 mt-1 shrink-0" />
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Welcome to the Settled Loans CRM</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
              Everything here is built around <span className="font-semibold">post-settlement care</span> — keeping your settled clients
              well looked after, spotting opportunities to help them again, and making sure no one falls through the cracks.
              The system tells you <span className="font-semibold">when</span> to act and <span className="font-semibold">what</span> to send;
              you do the actual sending so every client touch feels personal.
            </p>
          </div>
        </div>
      </div>

      {/* Demo loader */}
      {isAdmin && (
        <div className="bg-white dark:bg-slate-900 border-2 border-emerald-100 dark:border-emerald-900/40 rounded-3xl p-6 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-emerald-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Try it with sample data</h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                Click below to add two realistic demo clients to your loan book —
                <span className="font-semibold"> Sarah &amp; Tom Mitchell</span> (CBA, settled 3 months ago, scored you a 10 on NPS)
                and <span className="font-semibold"> Daniel Chen</span> (Bluestone, settled 6 months ago, ready for a specialist refinance review).
                Every layout becomes much easier to understand once there&apos;s data on the screen.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={loadDemo}
              disabled={seeding || removing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50 shadow-sm"
              data-testid="instructions-load-demo-btn"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              {demoCount > 0 ? 'Demo loaded ✓ — top up' : 'Load demo data'}
            </button>
            {demoCount > 0 && (
              <button
                type="button"
                onClick={removeDemo}
                disabled={seeding || removing}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-xs font-semibold disabled:opacity-50"
                data-testid="instructions-remove-demo-btn"
              >
                {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Remove demo data
              </button>
            )}
            {demoCount > 0 && (
              <span className="text-[11px] text-slate-500 ml-auto">
                {demoCount} demo loan{demoCount === 1 ? '' : 's'} on your book right now.
              </span>
            )}
          </div>
        </div>
      )}

      {/* The 5-minute walkthrough */}
      <Section
        n={1}
        title="The Loan book"
        icon={Building2}
        iconBg="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
      >
        <p>Your full settled book lives here. Each row is one loan; the colour-coded chips show the lender and lifecycle. Click a row to slide open the loan drawer with everything — client details, structure, property, commission, suppression and activity timeline.</p>
        <Tip>
          To add a new loan use <span className="font-semibold">New loan record</span> in the top right. The lender dropdown
          groups every major Australian lender — pick a <span className="font-mono">specialist</span> lender (Bluestone,
          MA Money, Pepper, Liberty, La Trobe, VMG, Resimac, Firstmac) and the system will automatically swap the 6-month
          touchpoint for the SPECIALIST_6M rate-review email.
        </Tip>
      </Section>

      <Section
        n={2}
        title="Action this week"
        icon={Zap}
        iconBg="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
      >
        <p>Your morning view. Shows everything urgent across the post-settlement book: overdue care touch points, fixed-rate expiries within 90 days, IO expiries, NPS detractors needing a recovery call, and Specialist clients hitting their refinance window. Knock these out first.</p>
      </Section>

      <Section
        n={3}
        title="Opportunities"
        icon={Target}
        iconBg="bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300"
      >
        <p>Open pipeline of refinances, top-ups and consolidations that the system has identified from your settled book. Each opportunity carries a value, a stage, and the trigger that surfaced it. Move them through identified → contacted → quoted → application → settled.</p>
        <Tip>
          The Specialist 6-month refinance pathway shows up here automatically — exactly like Daniel Chen in the demo data.
        </Tip>
      </Section>

      <Section
        n={4}
        title="Care queue — the core workflow"
        icon={ListChecks}
        iconBg="bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
        highlight
      >
        <p>This is where you spend most of your time. Every settled loan auto-enrols into a 10-step care cadence over 24 months:</p>
        <table className="w-full text-[11px] text-left mt-2 mb-3 bg-slate-50 dark:bg-slate-900/40 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-800">
              <th className="px-2 py-1.5">When</th>
              <th className="px-2 py-1.5">Template</th>
              <th className="px-2 py-1.5">Purpose</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            <Cadence when="Day 1"   code="WELCOME_D0"     purpose="You're settled — congratulations." />
            <Cadence when="6 weeks" code="SETTLING_6W"    purpose="Quick check first repayments came out OK." />
            <Cadence when="3 mo"    code="CHECKIN_3M"     purpose="Stay in touch — no-pressure check-in." />
            <Cadence when="6 mo"    code="CHECK_6M / SPECIALIST_6M" purpose="6-mo review — specialist clients get the rate review." />
            <Cadence when="9 mo"    code="GETAHEAD_9M"    purpose="Offset / redraw tip to save interest." />
            <Cadence when="12 mo"   code="ANNUAL_12M"     purpose="Annual review — book a call." />
            <Cadence when="15 mo"   code="CHECKIN_15M"    purpose="Light-touch keep-in-touch." />
            <Cadence when="18 mo"   code="CHECK_18M"      purpose="Property values / consolidation review." />
            <Cadence when="21 mo"   code="CHECKIN_21M"    purpose="Quick hello." />
            <Cadence when="24 mo"   code="REVIEW_24M"     purpose="2-year review — book a call." />
          </tbody>
        </table>
        <p>Each row in the queue shows the client, the template code, and a blue <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 text-[10px] font-bold"><Copy className="w-3 h-3" />Copy email</span> button.</p>
        <Steps
          items={[
            'Click Copy email — the subject + body + advisory footer go to your clipboard, with the client\'s real name & lender already merged in.',
            'Open Gmail / Outlook / whatever you send from. Paste into a new mail to that client.',
            'Read it through, tweak if you want, hit Send.',
            'Come back to the Care queue and click Done to mark the touch point complete (or Snooze if it\'s not the right time).',
          ]}
        />
        <Tip type="amber">
          The system <span className="font-bold">never sends emails on its own</span> — every touch goes out from your own
          inbox so the relationship stays personal. The CRM just tells you when and gives you the words.
        </Tip>
      </Section>

      <Section
        n={5}
        title="No-contact"
        icon={AlertTriangle}
        iconBg="bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300"
      >
        <p>Settled loans that haven&apos;t had any logged contact in 90+ days. Catch &quot;stale&quot; clients before they drift away.</p>
      </Section>

      <Section
        n={6}
        title="Exceptions"
        icon={ShieldAlert}
        iconBg="bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
      >
        <p>System integrity dashboard. Daily reconciliation flags anything that looks off — missing care tasks, draft loans waiting for approval, NPS responses that breached the recovery SLA, queued messages stuck in the outbox, etc. Should always be near-zero. Anything here means the system found a problem and needs your eyes.</p>
      </Section>

      <Section
        n={7}
        title="Templates"
        icon={FileText}
        iconBg="bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300"
      >
        <p>Versioned library of the 11 on-brand emails the system uses for the care cadence. Pick any template, optionally pick a real client to render the merge fields against, and either:</p>
        <Steps
          items={[
            'Click Copy email text to grab the rendered version (this is the same thing the Care queue Copy button does).',
            'Or edit the subject/body and save — the old version is archived, the new version becomes Active.',
            'Click Sync new templates to pull in any new catalogue entries from a future release.',
          ]}
        />
        <Tip>
          The advisory footer (&quot;Always seek professional advice…&quot;) is auto-appended on every copy.
          You can&apos;t accidentally forget it.
        </Tip>
      </Section>

      <Section
        n={8}
        title="Closing a loan"
        icon={CheckCircle2}
        iconBg="bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
      >
        <p>When a loan&apos;s no longer active — paid off, refinanced away, or just closed — open the loan drawer, scroll to the bottom of the Overview tab, and click <span className="px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 text-[11px] font-semibold">Home loan closed</span>.</p>
        <p>Pick the reason (Discharged / Refinanced away / general Closed), add a note if useful, and the system:</p>
        <Steps
          items={[
            'Stops the care cadence — all pending touch points are cancelled.',
            'Marks the loan with a suppression banner so it\'s clearly out of rotation.',
            'Keeps the loan record on file forever for trail commission / clawback reporting — it\'s never deleted.',
            'Writes an immutable audit entry so you can prove who closed it and why.',
          ]}
        />
      </Section>

      <Section
        n={9}
        title="NPS — Net Promoter feedback"
        icon={UserCheck}
        iconBg="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
      >
        <p>Inside each loan&apos;s drawer there&apos;s an NPS tab where you can capture the client&apos;s 0-10 score after the 1-month touchpoint. The score routes the next communication automatically:</p>
        <ul className="list-disc list-inside text-xs space-y-1 ml-2 text-slate-600 dark:text-slate-300">
          <li><span className="font-semibold text-emerald-700 dark:text-emerald-300">9-10 Promoter</span> → REVIEW_REQUEST email asking for a Google review.</li>
          <li><span className="font-semibold text-amber-700 dark:text-amber-400">7-8 Passive</span> → NPS_PASSIVE email asking what would have made it a 10.</li>
          <li><span className="font-semibold text-rose-700 dark:text-rose-300">0-6 Detractor</span> → an exception is raised and you have 48 hours to call them back. Use NPS_RECOVERY to apologise and book the call.</li>
        </ul>
      </Section>

      {/* Closing block */}
      <div className="mt-8 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 flex items-start gap-3">
        <Mail className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" />
        <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
          <p className="font-semibold text-slate-800 dark:text-slate-100">That&apos;s the whole system.</p>
          <p className="mt-1">If anything ever looks off, the Exceptions tab is your safety net — it will tell you what&apos;s broken before a client does.</p>
        </div>
      </div>
    </div>
  );
};

// ── helpers ──
const Section = ({ n, title, icon: Icon, iconBg, children, highlight = false }) => (
  <section className={`bg-white dark:bg-slate-900 border ${highlight ? 'border-blue-200 dark:border-blue-900/50 ring-1 ring-blue-100 dark:ring-blue-950/40' : 'border-slate-200 dark:border-slate-800'} rounded-2xl p-5 mb-4`}>
    <header className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="flex items-baseline gap-2 flex-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Step {n}</span>
        <h3 className="text-base font-bold text-slate-900 dark:text-white">{title}</h3>
      </div>
    </header>
    <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed space-y-2 ml-12">
      {children}
    </div>
  </section>
);

const Tip = ({ children, type = 'blue' }) => {
  const palette = type === 'amber'
    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 text-amber-900 dark:text-amber-200'
    : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/50 text-blue-900 dark:text-blue-200';
  return (
    <div className={`mt-2 border-l-2 ${palette} rounded-r-md px-3 py-2 text-[11px] leading-relaxed`}>
      {children}
    </div>
  );
};

const Steps = ({ items }) => (
  <ol className="space-y-1.5 mt-2 ml-0.5">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
        <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-500" />
        <span>{it}</span>
      </li>
    ))}
  </ol>
);

const Cadence = ({ when, code, purpose }) => (
  <tr>
    <td className="px-2 py-1.5 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{when}</td>
    <td className="px-2 py-1.5 font-mono text-blue-700 dark:text-blue-300 whitespace-nowrap">{code}</td>
    <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{purpose}</td>
  </tr>
);

export default InstructionsTab;
